/**
 * Checklist answer evaluation — the "DETECT" step of
 * CHECK -> DETECT -> RECORD -> ESCALATE.
 *
 * why this lives in shared: the device must decide "4.2 bar is below the
 * minimum of 5 bar" while completely offline, and the server must reach the
 * identical verdict when that answer syncs hours later. Two implementations
 * would eventually disagree, and the disagreement would show up as an
 * abnormality appearing or vanishing on sync. There is one implementation.
 *
 * Pure and synchronous: no I/O, no framework, exhaustively unit tested.
 */

import type { AnswerResult, ChecklistItemType } from './domain';

export class EvaluationError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'EvaluationError';
    this.code = code;
  }
}

export interface EvaluableItem {
  id: string;
  itemType: ChecklistItemType | string;
  isRequired: boolean;
  minValue: number | null;
  maxValue: number | null;
  targetValue: number | null;
  tolerance: number | null;
  recordOnly: boolean;
  unit: string | null;
  expectedValue: string | null;
  isSafetyCritical: boolean;
  failureBehaviour: string;
  requiresPhoto: boolean;
  requiresPhotoOnFail: boolean;
  options?: Array<{ value: string; label: string; result: string }>;
}

export interface RawAnswer {
  valueText?: string | null;
  valueNumber?: number | null;
  valueBool?: boolean | null;
  valueList?: string[] | null;
  /** Only honoured for items that are not safety-critical. */
  markedNotApplicable?: boolean;
  comment?: string | null;
  evidenceCount?: number;
}

export interface Evaluation {
  result: AnswerResult;
  isAbnormal: boolean;
  /** Operator-facing explanation, e.g. "4.2 bar is below the minimum of 5 bar." */
  reason: string | null;
}

/** Effective bounds, whether expressed as min/max or target +/- tolerance. */
export function numericBounds(item: EvaluableItem): { min: number | null; max: number | null } {
  if (item.recordOnly) return { min: null, max: null };
  if (item.targetValue != null && item.tolerance != null) {
    return { min: item.targetValue - item.tolerance, max: item.targetValue + item.tolerance };
  }
  return { min: item.minValue, max: item.maxValue };
}

function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}

function withUnit(n: number, unit: string | null): string {
  return unit ? `${formatNumber(n)} ${unit}` : formatNumber(n);
}

function missing(): EvaluationError {
  return new EvaluationError('ANSWER_REQUIRED', 'Please answer this task before continuing.');
}

export function evaluateAnswer(item: EvaluableItem, raw: RawAnswer): Evaluation {
  if (raw.markedNotApplicable) {
    // why: a safety-critical task is the one thing an operator must not be able
    // to wave away. Anything else may legitimately not apply on a given shift.
    if (item.isSafetyCritical) {
      throw new EvaluationError(
        'SAFETY_ITEM_NOT_SKIPPABLE',
        'This is a safety-critical check and cannot be marked not applicable.',
      );
    }
    return { result: 'NA', isAbnormal: false, reason: null };
  }

  switch (item.itemType) {
    case 'PASS_FAIL': {
      if (raw.valueBool == null) throw missing();
      return raw.valueBool
        ? { result: 'PASS', isAbnormal: false, reason: null }
        : { result: 'FAIL', isAbnormal: true, reason: 'Marked FAIL by the operator.' };
    }

    case 'YES_NO':
    case 'CONFIRMATION': {
      if (raw.valueBool == null) throw missing();
      // A confirmation not given is an abnormality: the standard step did not happen.
      return raw.valueBool
        ? { result: 'PASS', isAbnormal: false, reason: null }
        : {
            result: 'FAIL',
            isAbnormal: true,
            reason: 'The operator did not confirm this step.',
          };
    }

    case 'NUMERIC':
    case 'COUNTER': {
      if (raw.valueNumber == null || Number.isNaN(raw.valueNumber)) throw missing();
      const { min, max } = numericBounds(item);

      if (min == null && max == null) {
        // Nothing to judge against — record the reading, flag nothing.
        return { result: 'NORMAL', isAbnormal: false, reason: null };
      }
      if (min != null && raw.valueNumber < min) {
        return {
          result: 'ABNORMAL',
          isAbnormal: true,
          reason: `${withUnit(raw.valueNumber, item.unit)} is below the minimum of ${withUnit(min, item.unit)}.`,
        };
      }
      if (max != null && raw.valueNumber > max) {
        return {
          result: 'ABNORMAL',
          isAbnormal: true,
          reason: `${withUnit(raw.valueNumber, item.unit)} is above the maximum of ${withUnit(max, item.unit)}.`,
        };
      }
      return { result: 'NORMAL', isAbnormal: false, reason: null };
    }

    case 'DROPDOWN': {
      if (!raw.valueText) throw missing();
      const option = item.options?.find((o) => o.value === raw.valueText);
      if (!option) {
        throw new EvaluationError(
          'INVALID_OPTION',
          'That is not one of the available options for this task.',
        );
      }
      const abnormal = option.result === 'ABNORMAL' || option.result === 'FAIL';
      return {
        result: abnormal ? 'ABNORMAL' : (option.result as AnswerResult),
        isAbnormal: abnormal,
        reason: abnormal ? `Operator selected "${option.label}".` : null,
      };
    }

    case 'MULTI_SELECT': {
      const selected = raw.valueList ?? [];
      if (selected.length === 0 && item.isRequired) throw missing();

      const chosen = (item.options ?? []).filter((o) => selected.includes(o.value));
      if (chosen.length !== selected.length) {
        throw new EvaluationError(
          'INVALID_OPTION',
          'One or more selected options are not valid for this task.',
        );
      }
      // Worst outcome wins: any abnormal selection makes the whole answer abnormal.
      const abnormal = chosen.filter((o) => o.result === 'ABNORMAL' || o.result === 'FAIL');
      return abnormal.length > 0
        ? {
            result: 'ABNORMAL',
            isAbnormal: true,
            reason: `Operator selected ${abnormal.map((o) => `"${o.label}"`).join(', ')}.`,
          }
        : { result: 'NORMAL', isAbnormal: false, reason: null };
    }

    case 'TEXT': {
      if (item.isRequired && !raw.valueText?.trim()) throw missing();
      // Free text carries no automatic judgement — an operator flags a concern
      // by raising a Problem Tag, not by phrasing.
      return { result: 'NORMAL', isAbnormal: false, reason: null };
    }

    case 'PHOTO': {
      if ((raw.evidenceCount ?? 0) === 0 && item.isRequired) {
        throw new EvaluationError('PHOTO_REQUIRED', 'This task requires a photo.');
      }
      return { result: 'NORMAL', isAbnormal: false, reason: null };
    }

    case 'SIGNATURE': {
      if (!raw.valueText?.trim() && item.isRequired) {
        throw new EvaluationError('SIGNATURE_REQUIRED', 'This task requires a signature.');
      }
      return { result: 'NORMAL', isAbnormal: false, reason: null };
    }

    default:
      throw new EvaluationError(
        'UNSUPPORTED_ITEM_TYPE',
        `Unsupported task type "${item.itemType}".`,
      );
  }
}

/**
 * What the engine must additionally demand once an answer came back abnormal
 * (directive §53). Returned rather than thrown so the UI can present every
 * outstanding requirement at once instead of one error at a time.
 */
export interface FollowUpRequirement {
  needsComment: boolean;
  needsEvidence: boolean;
  needsProblemTag: boolean;
  blocks: boolean;
}

export function followUpFor(
  item: EvaluableItem,
  evaluation: Evaluation,
  raw: RawAnswer,
): FollowUpRequirement {
  const requirement: FollowUpRequirement = {
    needsComment: false,
    needsEvidence: false,
    needsProblemTag: false,
    blocks: false,
  };

  // A photo demanded unconditionally applies regardless of outcome.
  if (item.requiresPhoto && (raw.evidenceCount ?? 0) === 0) {
    requirement.needsEvidence = true;
  }

  if (!evaluation.isAbnormal) return requirement;

  const hasComment = Boolean(raw.comment?.trim());
  const hasEvidence = (raw.evidenceCount ?? 0) > 0;
  const evidenceOnFail = item.requiresPhotoOnFail ? !hasEvidence : requirement.needsEvidence;

  switch (item.failureBehaviour) {
    case 'NONE':
      return requirement;
    case 'COMMENT':
      return { ...requirement, needsComment: !hasComment };
    case 'EVIDENCE':
      return { ...requirement, needsComment: !hasComment, needsEvidence: evidenceOnFail };
    case 'PROBLEM_TAG':
      return {
        ...requirement,
        needsComment: !hasComment,
        needsEvidence: evidenceOnFail,
        needsProblemTag: true,
      };
    case 'BLOCK':
      return {
        ...requirement,
        needsComment: !hasComment,
        needsEvidence: evidenceOnFail,
        needsProblemTag: true,
        blocks: true,
      };
    default:
      return { ...requirement, needsComment: !hasComment };
  }
}

export function describeRequirements(requirement: FollowUpRequirement): string[] {
  const out: string[] = [];
  if (requirement.needsComment) out.push('Add a short note describing what you found.');
  if (requirement.needsEvidence) out.push('Attach a photo of the condition.');
  if (requirement.needsProblemTag) out.push('Raise a Problem Tag for this abnormality.');
  return out;
}
